// ============================================================================
// OINP — Ontario Workforce Priority assessment wizard.
// Three branches selected at step 1; the step count re-bases per branch
// (job offer = 9, self-employed physician = 4, no offer yet = 2).
// ============================================================================

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Wrapper, Progress, Eyebrow, StepTitle, StepSubtitle, FieldLabel,
  OptionGrid, OptionCard, TextInput, InlineRow, ErrorText, HelpLink,
  Note, NavRow, BackBtn, NextBtn, PrimaryBtn,
} from '../assessments/oinp/ui';
import { translator } from '../assessments/oinp/i18n';
import {
  REGIONS, MONTH_BANDS, WAGE_BANDS, EDUCATION_LEVELS, CANADIAN_CREDENTIAL_COUNTS,
  CLB_BANDS, LEGAL_STATUSES, EARNINGS_BANDS, EXPERIENCE_OPTIONS, CPSO_CERTIFICATES,
} from '../assessments/oinp/rules';
import { resolveNoc, isValidNocCode, searchNocByTitle, lookupNocCode, NOC_LOOKUP_URL } from '../assessments/oinp/noc';
import { evaluateCandidate } from '../assessments/oinp/engine';

// Physicians are scored on the same factors as job-offer applicants except
// hourly wage, so they need the same questions — asking only OHIP/CPSO/practice
// months would leave 8 of the 11 scoring factors unanswered and no score could
// be published. They skip wage, and their Ontario experience comes from
// cumulative medical practice rather than months in a job offer position.
const BRANCH_STEPS = {
  employer: ['path', 'noc', 'region', 'wage', 'experience', 'months', 'education', 'language', 'status'],
  physician: ['path', 'ohip', 'cpso', 'practice', 'noc', 'region', 'education', 'language', 'status'],
  neither: ['path', 'notify'],
};

const STEP_EYEBROWS = {
  path: 'PATH', noc: 'NOC', region: 'REGION', wage: 'WAGE', experience: 'EXPERIENCE',
  months: 'MONTHS', education: 'EDUCATION', language: 'LANGUAGE', status: 'STATUS',
  ohip: 'OHIP', cpso: 'CPSO', practice: 'PRACTICE', notify: 'NOTIFY ME',
};

const MONTH_LABEL_KEYS = { '0-5': 'months05', '6-12': 'months612', '13-24': 'months1324', '25+': 'months25' };

const EDUCATION_LABEL_KEYS = {
  'doctorate': 'eduDoctorate',
  'masters': 'eduMasters',
  'univ-cert-above-bachelor': 'eduUnivAboveBachelor',
  'bachelor': 'eduBachelor',
  'ontario-college-grad-cert': 'eduOntarioGradCert',
  'univ-cert-below-bachelor': 'eduUnivBelowBachelor',
  'college-diploma': 'eduCollegeDiploma',
  'apprenticeship': 'eduApprenticeship',
  'high-school': 'eduHighSchool',
  'less-than-high-school': 'eduLessThanHighSchool',
};

const CLB_LABEL_KEYS = {
  '4-or-lower': 'clb4OrLower', '5': 'clb5', '6': 'clb6', '7': 'clb7',
  '8': 'clb8', '9+': 'clb9Plus', 'untested': 'clbUntested',
};

const STATUS_LABEL_KEYS = {
  'work-permit': 'statusWorkPermit', 'study-permit': 'statusStudyPermit',
  'maintained': 'statusMaintained', 'visitor': 'statusVisitor',
  'no-status': 'statusNone', 'outside-canada': 'statusOutside',
};

const EARNINGS_LABEL_KEYS = {
  '70k+': 'earnings70k', '50-69999': 'earnings50to70', '30-49999': 'earnings30to50',
  'under-30k': 'earningsUnder30', 'none': 'earningsNone',
};

const EXPERIENCE_DETAIL_KEYS = {
  'six-months-exact-position': 'expSixMonthsDetail',
  'recent-grad-three-months': 'expRecentGradDetail',
  'two-years-cumulative': 'expTwoYearsDetail',
  'licensed-to-practise': 'expLicensedDetail',
  'none-yet': 'expNoneDetail',
};

const emptyAnswers = {
  path: '',
  noc: '', region: '', wage: '',
  experience: '', monthsInPosition: '', monthsInOntario: '',
  education: '', canadianCredentials: '', eca: '',
  clb: '', bothLanguages: '',
  legalStatus: '', earnings: '',
  ohip: '', cpso: '', practiceMonths: '',
  notifyEmail: '',
};

const OINPCalculator = ({
  onComplete,
  onBack,
  onNotifyMe,
  darkMode = false,
  scheme = 'blue',
  language = 'en',
}) => {
  const tr = translator(language);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState(emptyAnswers);
  const [nocDraft, setNocDraft] = useState('');
  const [nocQuery, setNocQuery] = useState('');
  const [nocError, setNocError] = useState('');
  const [nocChecking, setNocChecking] = useState(false);
  const [nocTitle, setNocTitle] = useState('');
  const [wageError, setWageError] = useState('');

  const set = (field, value) => setAnswers(prev => ({ ...prev, [field]: value }));

  // Until a path is picked we show the longest branch, so the progress bar
  // doesn't jump backwards once they choose.
  const steps = BRANCH_STEPS[answers.path] || BRANCH_STEPS.employer;
  const current = steps[Math.min(step, steps.length - 1)];
  const total = steps.length;
  const resolvedNoc = useMemo(() => resolveNoc(answers.noc), [answers.noc]);

  // Occupation search runs server-side (the official index is ~900 KB and has no
  // place in a public widget bundle). Debounced, and each new keystroke aborts
  // the in-flight request so results can't arrive out of order.
  const [nocSuggestions, setNocSuggestions] = useState([]);
  const [nocSearching, setNocSearching] = useState(false);
  const nocAbortRef = useRef(null);

  useEffect(() => {
    const q = nocQuery.trim();
    if (q.length < 2) {
      setNocSuggestions([]);
      setNocSearching(false);
      return undefined;
    }

    setNocSearching(true);
    const timer = setTimeout(async () => {
      if (nocAbortRef.current) nocAbortRef.current.abort();
      const controller = new AbortController();
      nocAbortRef.current = controller;
      const results = await searchNocByTitle(q, { signal: controller.signal });
      if (!controller.signal.aborted) {
        setNocSuggestions(results);
        setNocSearching(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [nocQuery]);

  useEffect(() => () => { if (nocAbortRef.current) nocAbortRef.current.abort(); }, []);

  /**
   * Accept a NOC code only if it is a REAL NOC 2021 unit group.
   *
   * Five digits is not enough. TEER and broad category are derived from the
   * code's own digits, so a plausible-looking but non-existent code (23100, say —
   * there are no 23xxx groups at all) still yields a TEER and a category, and the
   * user gets points for an occupation that does not exist. Checked against the
   * official 516-group index on the server.
   */
  const applyNoc = async code => {
    const candidate = String(code || '').trim();
    if (!isValidNocCode(candidate)) {
      setNocError(tr('nocInvalid'));
      return;
    }
    setNocError('');
    setNocChecking(true);
    const official = await lookupNocCode(candidate);
    setNocChecking(false);
    if (!official) {
      setNocError(tr('nocNotFound', { code: candidate }));
      return;
    }
    setNocTitle(official.title || '');
    set('noc', candidate);
  };

  const canProceed = (() => {
    switch (current) {
      case 'path': return !!answers.path;
      case 'noc': return !!resolvedNoc;
      case 'region': return !!answers.region;
      case 'wage': return answers.wage !== '' && Number.isFinite(Number(answers.wage)) && Number(answers.wage) >= 0;
      case 'experience': return !!answers.experience;
      case 'months': return !!answers.monthsInPosition && !!answers.monthsInOntario;
      case 'education': return !!answers.education && !!answers.canadianCredentials && !!answers.eca;
      case 'language': return !!answers.clb && !!answers.bothLanguages;
      case 'status': return !!answers.legalStatus && !!answers.earnings;
      case 'ohip': return !!answers.ohip;
      case 'cpso': return !!answers.cpso;
      case 'practice': return !!answers.practiceMonths;
      // The no-offer branch is a soft capture: continuing without an email is
      // allowed, we just have nothing to send them.
      case 'notify': return true;
      default: return true;
    }
  })();

  // Every step in the active branch answered — lets us surface an early
  // "See my results" without forcing a walk through the remaining screens.
  const allAnswered = steps.every(name => {
    switch (name) {
      case 'path': return !!answers.path;
      case 'noc': return !!resolvedNoc;
      case 'region': return !!answers.region;
      case 'wage': return answers.wage !== '';
      case 'experience': return !!answers.experience;
      case 'months': return !!answers.monthsInPosition && !!answers.monthsInOntario;
      case 'education': return !!answers.education && !!answers.canadianCredentials && !!answers.eca;
      case 'language': return !!answers.clb && !!answers.bothLanguages;
      case 'status': return !!answers.legalStatus && !!answers.earnings;
      case 'ohip': return !!answers.ohip;
      case 'cpso': return !!answers.cpso;
      case 'practice': return !!answers.practiceMonths;
      case 'notify': return true;
      default: return true;
    }
  });

  const finish = () => {
    const payload = { ...answers, wage: answers.wage === '' ? null : Number(answers.wage) };
    if (payload.path === 'neither' && payload.notifyEmail && onNotifyMe) {
      onNotifyMe(payload.notifyEmail);
    }
    onComplete(evaluateCandidate(payload), payload);
  };

  const goNext = () => {
    if (current === 'wage') {
      const value = Number(answers.wage);
      if (!Number.isFinite(value) || value < 0) { setWageError(tr('wageInvalid')); return; }
      setWageError('');
    }
    if (step >= total - 1) { finish(); return; }
    setStep(s => s + 1);
  };

  const goBack = () => {
    if (step === 0) { onBack && onBack(); return; }
    setStep(s => Math.max(0, s - 1));
  };

  const startOver = () => {
    setAnswers(emptyAnswers);
    setNocDraft('');
    setNocQuery('');
    setNocError('');
    setNocTitle('');
    setWageError('');
    setStep(0);
  };

  // ------------------------------------------------------------------ steps --
  const renderStep = () => {
    switch (current) {
      case 'path':
        return (
          <>
            <StepTitle $darkMode={darkMode}>{tr('pathTitle')}</StepTitle>
            <StepSubtitle $darkMode={darkMode}>{tr('pathSubtitle')}</StepSubtitle>
            <OptionGrid>
              {[
                ['employer', 'pathEmployer', 'pathEmployerDetail'],
                ['physician', 'pathPhysician', 'pathPhysicianDetail'],
                ['neither', 'pathNeither', 'pathNeitherDetail'],
              ].map(([id, titleKey, detailKey]) => (
                <OptionCard
                  key={id}
                  title={tr(titleKey)}
                  detail={tr(detailKey)}
                  selected={answers.path === id}
                  onClick={() => { setAnswers({ ...emptyAnswers, path: id }); setStep(0); }}
                  darkMode={darkMode}
                  scheme={scheme}
                />
              ))}
            </OptionGrid>
          </>
        );

      case 'noc':
        return (
          <>
            <StepTitle $darkMode={darkMode}>{tr('nocTitle')}</StepTitle>
            <StepSubtitle $darkMode={darkMode}>{tr('nocSubtitle')}</StepSubtitle>

            <TextInput
              $darkMode={darkMode}
              $scheme={scheme}
              value={nocQuery}
              placeholder={tr('nocSearchPlaceholder')}
              onChange={e => setNocQuery(e.target.value)}
            />
            {nocSuggestions.length > 0 && (
              <OptionGrid style={{ marginTop: 8 }}>
                {nocSuggestions.map(s => (
                  <OptionCard
                    key={s.code}
                    title={s.title}
                    // Show which example title matched, so "PSW" visibly resolves
                    // to the formal unit group rather than looking like a guess.
                    detail={s.matchedOn && s.matchedOn.toLowerCase() !== s.title.toLowerCase()
                      ? tr('nocMatchedOn', { example: s.matchedOn })
                      : null}
                    badge={`NOC ${s.code}`}
                    selected={answers.noc === s.code}
                    onClick={() => { setNocTitle(s.title); applyNoc(s.code); }}
                    darkMode={darkMode}
                    scheme={scheme}
                  />
                ))}
              </OptionGrid>
            )}
            {nocSearching && nocSuggestions.length === 0 && (
              <Note $darkMode={darkMode} style={{ marginTop: 8 }}>{tr('nocSearching')}</Note>
            )}
            {!nocSearching && nocQuery.trim().length >= 2 && nocSuggestions.length === 0 && (
              <Note $darkMode={darkMode} style={{ marginTop: 8 }}>{tr('nocNoResults')}</Note>
            )}

            <FieldLabel $darkMode={darkMode}>{tr('nocCodeLabel')}</FieldLabel>
            <InlineRow>
              <TextInput
                $darkMode={darkMode}
                $scheme={scheme}
                value={nocDraft}
                inputMode="numeric"
                maxLength={5}
                placeholder="33103"
                onChange={e => { setNocDraft(e.target.value.replace(/\D/g, '')); setNocError(''); }}
                onKeyDown={e => { if (e.key === 'Enter') applyNoc(nocDraft); }}
              />
              <NextBtn type="button" $darkMode={darkMode} $scheme={scheme} disabled={nocChecking} onClick={() => applyNoc(nocDraft)}>
                {nocChecking ? tr('nocChecking') : tr('nocApply')}
              </NextBtn>
            </InlineRow>
            {nocError && <ErrorText>{nocError}</ErrorText>}
            <HelpLink $darkMode={darkMode} href={NOC_LOOKUP_URL} target="_blank" rel="noopener noreferrer">
              {tr('nocLookupLink')}
            </HelpLink>

            {resolvedNoc && (
              <Note $darkMode={darkMode} style={{ marginTop: 12 }}>
                {tr('nocResolved', {
                  code: resolvedNoc.code,
                  teer: resolvedNoc.teer,
                  category: resolvedNoc.broadCategoryLabel,
                })}
                {nocTitle && <><br />{nocTitle}</>}
              </Note>
            )}
          </>
        );

      case 'region':
        return (
          <>
            <StepTitle $darkMode={darkMode}>
              {tr(answers.path === 'physician' ? 'regionTitlePhysician' : 'regionTitle')}
            </StepTitle>
            <StepSubtitle $darkMode={darkMode}>
              {tr(answers.path === 'physician' ? 'regionSubtitlePhysician' : 'regionSubtitle')}
            </StepSubtitle>
            <OptionGrid $cols={2}>
              {REGIONS.map(region => (
                <OptionCard
                  key={region.id}
                  title={tr(region.labelKey)}
                  detail={tr('regionPoints', { points: region.points })}
                  selected={answers.region === region.id}
                  onClick={() => set('region', region.id)}
                  darkMode={darkMode}
                  scheme={scheme}
                />
              ))}
            </OptionGrid>
          </>
        );

      case 'wage':
        return (
          <>
            <StepTitle $darkMode={darkMode}>{tr('wageTitle')}</StepTitle>
            <StepSubtitle $darkMode={darkMode}>{tr('wageSubtitle')}</StepSubtitle>
            <InlineRow>
              <TextInput
                $darkMode={darkMode}
                $scheme={scheme}
                value={answers.wage}
                inputMode="decimal"
                placeholder={tr('wagePlaceholder')}
                onChange={e => { set('wage', e.target.value.replace(/[^\d.]/g, '')); setWageError(''); }}
              />
            </InlineRow>
            {wageError && <ErrorText>{wageError}</ErrorText>}
            <Note $darkMode={darkMode} style={{ marginTop: 12 }}>
              <strong>{tr('wageBandsLabel')}: </strong>
              {WAGE_BANDS.bands.map(b => `${b.label} → ${b.points}`).join(' · ')}
            </Note>
          </>
        );

      case 'experience':
        return (
          <>
            <StepTitle $darkMode={darkMode}>{tr('experienceTitle')}</StepTitle>
            <StepSubtitle $darkMode={darkMode}>{tr('experienceSubtitle')}</StepSubtitle>
            <OptionGrid>
              {EXPERIENCE_OPTIONS.map(option => (
                <OptionCard
                  key={option.id}
                  title={tr(option.labelKey)}
                  detail={tr(EXPERIENCE_DETAIL_KEYS[option.id])}
                  selected={answers.experience === option.id}
                  onClick={() => set('experience', option.id)}
                  darkMode={darkMode}
                  scheme={scheme}
                />
              ))}
            </OptionGrid>
          </>
        );

      case 'months':
        return (
          <>
            <StepTitle $darkMode={darkMode}>{tr('monthsTitle')}</StepTitle>
            <StepSubtitle $darkMode={darkMode}>{tr('monthsSubtitle')}</StepSubtitle>

            <FieldLabel $darkMode={darkMode}>{tr('monthsInPositionLabel')}</FieldLabel>
            <OptionGrid $cols={2}>
              {MONTH_BANDS.map(band => (
                <OptionCard
                  key={`pos-${band}`}
                  title={tr(MONTH_LABEL_KEYS[band])}
                  selected={answers.monthsInPosition === band}
                  onClick={() => set('monthsInPosition', band)}
                  darkMode={darkMode}
                  scheme={scheme}
                />
              ))}
            </OptionGrid>

            <FieldLabel $darkMode={darkMode}>{tr('monthsInOntarioLabel')}</FieldLabel>
            <OptionGrid $cols={2}>
              {MONTH_BANDS.map(band => (
                <OptionCard
                  key={`ont-${band}`}
                  title={tr(MONTH_LABEL_KEYS[band])}
                  selected={answers.monthsInOntario === band}
                  onClick={() => set('monthsInOntario', band)}
                  darkMode={darkMode}
                  scheme={scheme}
                />
              ))}
            </OptionGrid>
          </>
        );

      case 'education':
        return (
          <>
            <StepTitle $darkMode={darkMode}>{tr('educationTitle')}</StepTitle>
            <OptionGrid $cols={2}>
              {EDUCATION_LEVELS.map(level => (
                <OptionCard
                  key={level}
                  title={tr(EDUCATION_LABEL_KEYS[level])}
                  selected={answers.education === level}
                  onClick={() => set('education', level)}
                  darkMode={darkMode}
                  scheme={scheme}
                />
              ))}
            </OptionGrid>

            <FieldLabel $darkMode={darkMode}>{tr('canadianCredsLabel')}</FieldLabel>
            <OptionGrid $cols={2}>
              {CANADIAN_CREDENTIAL_COUNTS.map(count => (
                <OptionCard
                  key={count}
                  title={count}
                  selected={answers.canadianCredentials === count}
                  onClick={() => set('canadianCredentials', count)}
                  darkMode={darkMode}
                  scheme={scheme}
                />
              ))}
            </OptionGrid>

            <FieldLabel $darkMode={darkMode}>{tr('ecaLabel')}</FieldLabel>
            <OptionGrid $cols={2}>
              {[['yes', 'yes'], ['no', 'no'], ['not-yet', 'notYet']].map(([id, key]) => (
                <OptionCard
                  key={id}
                  title={tr(key)}
                  selected={answers.eca === id}
                  onClick={() => set('eca', id)}
                  darkMode={darkMode}
                  scheme={scheme}
                />
              ))}
            </OptionGrid>
          </>
        );

      case 'language':
        return (
          <>
            <StepTitle $darkMode={darkMode}>{tr('languageTitle')}</StepTitle>
            <StepSubtitle $darkMode={darkMode}>{tr('languageSubtitle')}</StepSubtitle>
            <OptionGrid $cols={2}>
              {CLB_BANDS.map(band => (
                <OptionCard
                  key={band}
                  title={tr(CLB_LABEL_KEYS[band])}
                  selected={answers.clb === band}
                  onClick={() => set('clb', band)}
                  darkMode={darkMode}
                  scheme={scheme}
                />
              ))}
            </OptionGrid>

            <FieldLabel $darkMode={darkMode}>{tr('bothLanguagesLabel')}</FieldLabel>
            <OptionGrid $cols={2}>
              <OptionCard
                title={tr('bothLanguagesYes')}
                selected={answers.bothLanguages === 'both'}
                onClick={() => set('bothLanguages', 'both')}
                darkMode={darkMode}
                scheme={scheme}
              />
              <OptionCard
                title={tr('no')}
                selected={answers.bothLanguages === 'one'}
                onClick={() => set('bothLanguages', 'one')}
                darkMode={darkMode}
                scheme={scheme}
              />
            </OptionGrid>
          </>
        );

      case 'status':
        return (
          <>
            <StepTitle $darkMode={darkMode}>{tr('statusTitle')}</StepTitle>

            <FieldLabel $darkMode={darkMode}>{tr('statusLabel')}</FieldLabel>
            <OptionGrid $cols={2}>
              {LEGAL_STATUSES.map(status => (
                <OptionCard
                  key={status}
                  title={tr(STATUS_LABEL_KEYS[status])}
                  detail={status === 'maintained' ? tr('statusMaintainedDetail') : null}
                  selected={answers.legalStatus === status}
                  onClick={() => set('legalStatus', status)}
                  darkMode={darkMode}
                  scheme={scheme}
                />
              ))}
            </OptionGrid>

            <FieldLabel $darkMode={darkMode}>{tr('earningsLabel')}</FieldLabel>
            <OptionGrid $cols={2}>
              {EARNINGS_BANDS.map(band => (
                <OptionCard
                  key={band}
                  title={tr(EARNINGS_LABEL_KEYS[band])}
                  selected={answers.earnings === band}
                  onClick={() => set('earnings', band)}
                  darkMode={darkMode}
                  scheme={scheme}
                />
              ))}
            </OptionGrid>
          </>
        );

      // ---- physician branch ----
      case 'ohip':
        return (
          <>
            <StepTitle $darkMode={darkMode}>{tr('ohipTitle')}</StepTitle>
            <OptionGrid $cols={2}>
              {[['yes', 'yes'], ['not-yet', 'notYet']].map(([id, key]) => (
                <OptionCard
                  key={id}
                  title={tr(key)}
                  selected={answers.ohip === id}
                  onClick={() => set('ohip', id)}
                  darkMode={darkMode}
                  scheme={scheme}
                />
              ))}
            </OptionGrid>
          </>
        );

      case 'cpso':
        return (
          <>
            <StepTitle $darkMode={darkMode}>{tr('cpsoTitle')}</StepTitle>
            <StepSubtitle $darkMode={darkMode}>{tr('cpsoSubtitle')}</StepSubtitle>
            <OptionGrid $cols={2}>
              {CPSO_CERTIFICATES.map(cert => (
                <OptionCard
                  key={cert.id}
                  title={tr(cert.labelKey)}
                  selected={answers.cpso === cert.id}
                  onClick={() => set('cpso', cert.id)}
                  darkMode={darkMode}
                  scheme={scheme}
                />
              ))}
            </OptionGrid>
          </>
        );

      case 'practice':
        return (
          <>
            <StepTitle $darkMode={darkMode}>{tr('practiceTitle')}</StepTitle>
            <OptionGrid $cols={2}>
              {MONTH_BANDS.map(band => (
                <OptionCard
                  key={band}
                  title={tr(MONTH_LABEL_KEYS[band])}
                  selected={answers.practiceMonths === band}
                  onClick={() => set('practiceMonths', band)}
                  darkMode={darkMode}
                  scheme={scheme}
                />
              ))}
            </OptionGrid>
          </>
        );

      // ---- no-offer branch ----
      case 'notify':
        return (
          <>
            <StepTitle $darkMode={darkMode}>{tr('neitherTitle')}</StepTitle>
            <StepSubtitle $darkMode={darkMode}>{tr('neitherBody')}</StepSubtitle>
            <Note $darkMode={darkMode}>{tr('neitherCta')}</Note>
            <TextInput
              $darkMode={darkMode}
              $scheme={scheme}
              type="email"
              value={answers.notifyEmail}
              placeholder="you@example.com"
              onChange={e => set('notifyEmail', e.target.value)}
            />
          </>
        );

      default:
        return null;
    }
  };

  const pct = Math.round(((step + 1) / total) * 100);
  const isLast = step >= total - 1;
  const showEarlyResults = !isLast && allAnswered;

  return (
    <Wrapper>
      <Progress
        label={tr('stepOf', { current: step + 1, total })}
        pct={pct}
        darkMode={darkMode}
        scheme={scheme}
        onStartOver={step > 0 ? startOver : null}
        startOverLabel={tr('startOver')}
      />

      <Eyebrow $darkMode={darkMode} $scheme={scheme}>
        {String(step + 1).padStart(2, '0')} · {STEP_EYEBROWS[current]}
      </Eyebrow>

      {renderStep()}

      {showEarlyResults && (
        <PrimaryBtn type="button" $darkMode={darkMode} $scheme={scheme} onClick={finish} style={{ marginTop: 18 }}>
          {tr('seeResults')}
        </PrimaryBtn>
      )}

      <NavRow>
        <BackBtn type="button" $darkMode={darkMode} onClick={goBack}>
          {step === 0 ? tr('cancel') : tr('back')}
        </BackBtn>
        <NextBtn type="button" $darkMode={darkMode} $scheme={scheme} disabled={!canProceed} onClick={goNext}>
          {isLast ? tr('seeResults') : tr('next')}
        </NextBtn>
      </NavRow>
    </Wrapper>
  );
};

export default OINPCalculator;
